// TransportController — wraps Tone's Transport (the one master clock,
// docs/04 §4.1–4.4). Owns tempo, the looping region, the metronome, and beat
// events for the UI. Tempo-first mode only in Phase 01.

import * as Tone from "tone";

export interface BeatInfo {
  bar: number;
  beat: number; // 0-based within the bar
  isDownbeat: boolean;
  time: number; // audio-context time of the beat
}

type BeatListener = (info: BeatInfo) => void;
type LoopHandler = (time: number) => void;

const BEATS_PER_BAR = 4; // 4/4 only for now

export class TransportController {
  private readonly transport = Tone.getTransport();
  private readonly metro: Tone.MembraneSynth;
  private readonly countMetro: Tone.MembraneSynth;
  private metroRepeatId: number | null = null;
  private metronomeEnabled: boolean;
  private bars: number;
  private readonly beatListeners = new Set<BeatListener>();

  constructor(bpm: number, bars: number, metronomeEnabled: boolean, metroVolumeDb: number) {
    this.bars = bars;
    this.metronomeEnabled = metronomeEnabled;

    this.transport.bpm.value = bpm;
    this.transport.loop = true;
    this.transport.loopStart = 0;
    this.transport.loopEnd = `${bars}m`;

    // Dedicated click voice for the metronome (kept off the instrument bus).
    const clickConfig = {
      pitchDecay: 0.008,
      octaves: 2,
      envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.02 },
    } as const;
    this.metro = new Tone.MembraneSynth(clickConfig).toDestination();
    this.metro.volume.value = metroVolumeDb;
    // Separate voice for count-in clicks so they never collide with the
    // metronome's already-scheduled (lookahead) triggers when we restart the
    // transport — that would violate Tone's monotonic-time scheduling.
    this.countMetro = new Tone.MembraneSynth(clickConfig).toDestination();
    this.countMetro.volume.value = metroVolumeDb;

    this.scheduleMetronome();
  }

  /** Click + beat-event emitter on every quarter note. */
  private scheduleMetronome(): void {
    this.metroRepeatId = this.transport.scheduleRepeat((time) => {
      const pos = this.transport.position.toString(); // "bar:beat:sixteenth"
      const parts = pos.split(":");
      const bar = Number.parseInt(parts[0] ?? "0", 10);
      const beat = Number.parseInt(parts[1] ?? "0", 10);
      const isDownbeat = beat === 0;

      if (this.metronomeEnabled) {
        this.metro.triggerAttackRelease(isDownbeat ? "C3" : "C2", "32n", time, isDownbeat ? 1 : 0.6);
      }

      // Sync the visual beat pulse to the audio via Tone.Draw.
      const info: BeatInfo = { bar, beat, isDownbeat, time };
      Tone.getDraw().schedule(() => {
        for (const l of this.beatListeners) l(info);
      }, time);
    }, "4n");
  }

  /** Count-in click on the dedicated voice (transport stopped during count-in). */
  countClickAt(isDownbeat: boolean, time: number): void {
    this.countMetro.triggerAttackRelease(isDownbeat ? "C3" : "C2", "32n", time, isDownbeat ? 1 : 0.6);
  }

  onBeat(listener: BeatListener): () => void {
    this.beatListeners.add(listener);
    return () => this.beatListeners.delete(listener);
  }

  /** Fire once the next time the loop wraps, then auto-unsubscribe. */
  onLoopOnce(handler: LoopHandler): () => void {
    const wrapped = (time: number) => {
      this.transport.off("loop", wrapped);
      handler(time);
    };
    this.transport.on("loop", wrapped);
    return () => this.transport.off("loop", wrapped);
  }

  /** Fire on every loop wrap until unsubscribed. */
  onLoop(handler: LoopHandler): () => void {
    this.transport.on("loop", handler);
    return () => this.transport.off("loop", handler);
  }

  /** Current playhead position within the loop, in beats (0 .. bars*beatsPerBar). */
  currentLoopBeats(): number {
    const parts = this.transport.position.toString().split(":");
    const bar = Number.parseInt(parts[0] ?? "0", 10);
    const beat = Number.parseInt(parts[1] ?? "0", 10);
    const sixteenth = Number.parseFloat(parts[2] ?? "0");
    return bar * BEATS_PER_BAR + beat + sixteenth / 4;
  }

  scheduleOnce(callback: (time: number) => void, time: Tone.Unit.Time): number {
    return this.transport.scheduleOnce(callback, time);
  }

  clearScheduled(id: number): void {
    this.transport.clear(id);
  }

  // --- Tempo / length ---
  setBpm(bpm: number): void {
    this.transport.bpm.value = bpm;
  }
  getBpm(): number {
    return this.transport.bpm.value;
  }
  setBars(bars: number): void {
    this.bars = bars;
    this.transport.loopEnd = `${bars}m`;
  }
  getBars(): number {
    return this.bars;
  }
  get beatsPerBar(): number {
    return BEATS_PER_BAR;
  }
  get loopDurationSec(): number {
    return Tone.Time(`${this.bars}m`).toSeconds();
  }
  get loopEndPosition(): Tone.Unit.Time {
    return `${this.bars}m`;
  }

  // --- Metronome ---
  setMetronome(on: boolean): void {
    this.metronomeEnabled = on;
  }
  setMetronomeVolume(db: number): void {
    this.metro.volume.value = db;
  }

  // --- Run control ---
  start(time?: Tone.Unit.Time): void {
    this.transport.start(time);
  }
  /** Start at an explicit audio time from a given loop offset (for recording). */
  startAt(time: number, offset: Tone.Unit.Time): void {
    this.transport.start(time, offset);
  }
  stop(): void {
    this.transport.stop();
  }
  resetPosition(): void {
    this.transport.position = 0;
  }
  get isRunning(): boolean {
    return this.transport.state === "started";
  }
  get progress(): number {
    return this.transport.progress;
  }

  dispose(): void {
    if (this.metroRepeatId !== null) this.transport.clear(this.metroRepeatId);
    this.beatListeners.clear();
    this.metro.dispose();
    this.countMetro.dispose();
  }
}
